<template>
  <div class="p-0 overflow-hidden" ref="containerRef">
    <Carousel class="w-100 p-0 m-0 top-0" v-model="slideIndex" :autoplay="8000">
      <Slide class="p-0 slide">
        <div class="ci-green-bg w-100 position-relative">
          <div class="py-5 px-2 text-center mt-5">
            <h1 class="pt-2 pb-3 px-1 text-white fw-bold">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_1_HEADER"
                )
              }}
            </h1>
            <span class="text-lime-green fs-3 lh-1">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_1_SUBHEADER"
                )
              }}
            </span>
          </div>
          <dotlottie-wc
            :src="useAsset('img/credit-line/credit-card/use-card-today.lottie')"
            :autoplay="true"
            :loop="true"
            alt="use card today"
            class="w-100 mt-auto mb-0 position-absolute bottom-0"
            id="slide-one-animation"
          />
        </div>
      </Slide>

      <Slide class="p-0 slide">
        <div class="ci-green-bg w-100">
          <div class="py-5 px-2 mt-5">
            <h1 class="pt-3 pb-3 px-1 text-center text-white fw-bold fs-2">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_2_HEADER"
                )
              }}
            </h1>
            <div class="mt-2">
              <dotlottie-wc
                :src="
                  useAsset(
                    'img/credit-line/credit-card/' +
                      getEcommerceExamplesLottiePath()
                  )
                "
                :autoplay="true"
                :loop="true"
                alt="use card today"
                class="w-100"
                id="slide-two-animation"
              />
            </div>
            <p class="text-white-50 mt-3 small lh-1">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_2_NOTICE"
                )
              }}
            </p>
          </div>
        </div>
      </Slide>

      <Slide class="p-0 slide">
        <div class="ci-green-bg w-100">
          <div class="py-5 px-2 mt-5">
            <h1 class="pt-3 pb-5 px-1 text-center text-white fw-bold fs-2">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_3_HEADER"
                )
              }}
            </h1>
            <div class="mt-5">
              <dotlottie-wc
                :src="
                  useAsset('img/credit-line/credit-card/show-details.lottie')
                "
                :autoplay="true"
                :loop="true"
                alt="show card details"
                class="w-100"
                id="slide-three-animation"
              />
            </div>
          </div>
        </div>
      </Slide>

      <Slide class="p-0 slide">
        <div class="ci-green-bg w-100">
          <div class="py-5 px-2 mt-5">
            <h1 class="pt-3 pb-5 px-1 text-center text-white fw-bold fs-2">
              {{
                t(
                  "PAYJOY_CARD_VIEWS.VIRTUAL_CARD_CONFIRM_CREATE.SLIDE_4_HEADER"
                )
              }}
            </h1>
            <div class="mt-2">
              <dotlottie-wc
                :src="
                  useAsset('img/credit-line/credit-card/copy-details.lottie')
                "
                :autoplay="true"
                :loop="true"
                alt="copy details"
                class="w-100 px-1"
                id="slide-four-animation"
              />
            </div>
          </div>
        </div>
      </Slide>

      <template #addons="{ currentSlide, slidesCount }">
        <section class="position-absolute top-0 w-100 d-flex flex-row">
          <div class="mt-3" id="steps">
            <span
              v-for="index in slidesCount"
              :key="index"
              :class="['step', { completed: index - 1 <= currentSlide }]"
            />
          </div>
        </section>
        <div class="position-absolute top-0 end-0 me-3 mt-4">
          <button class="bg-transparent border-0 mt-4" @click="handleClose">
            <i class="fa fa-close fs-1 text-white" />
          </button>
        </div>
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useRoute, useRouter } from "vue-router";
import { Carousel, Slide } from "vue3-carousel";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { resizeComponentHelper } from "@/apps/credit-line/helpers/resizeComponent.helper";
import "@lottiefiles/dotlottie-wc";

const slideIndex = ref(0);
const router = useRouter();
const { t } = useI18n();
const defaultCountry = "mx";
const containerRef = ref();

const ecommerceExamplesLottieCountries = {
  mx: "mx-ecommerce-examples.lottie",
  co: "co-ecommerce-examples.lottie",
};
const country = (
  (useRoute().query?.["country"] ?? "MX") as string
).toLowerCase();

const getEcommerceExamplesLottiePath = () => {
  return (
    ecommerceExamplesLottieCountries[country] ||
    ecommerceExamplesLottieCountries[defaultCountry]
  );
};

const handleClose = () => {
  router.push({
    name: "CreditCardDetailsView",
    query: { defaultType: "VIRTUAL" },
  });
};

onMounted(() => {
  resizeComponentHelper(containerRef);
  document.querySelectorAll(".slide > div:first-child")?.forEach((slide) => {
    slide.style.height = window.innerHeight + "px";
  });
  const adjustCarouselTrack = () => {
    const track = document.getElementsByClassName(
      "carousel__track"
    )[0] as HTMLElement;
    if (track) {
      track.style.marginBottom = "0";
    }
  };

  const interval = setInterval(adjustCarouselTrack, 500);

  // Cleanup interval on component unmount
  return () => clearInterval(interval);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

body {
  overflow-y: hidden !important;
}

.slide {
  width: 100%;
}

.ci-green-bg {
  background: $pj-color-green;
}

.text-lime-green {
  color: $pj-color-lime-green;
}

#steps {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1rem;
  width: 100%;

  .step {
    border-radius: 9px;
    height: 9px;
    background-color: rgba(255, 255, 255, 0.5);
    flex-grow: 1;
    position: relative;
    overflow: hidden;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 100%;
      background-color: white;
      transition: width 0.3s;
    }

    &:not(.completed)::after {
      width: 0;
    }

    &.completed::after {
      width: 100%;
    }
  }
}

.slide {
  ul {
    list-style-type: disc;

    li {
      text-align: left;
      margin-bottom: 2rem;
      font-size: 20px;
      font-weight: 400;
      line-height: 138%;
    }
  }
}

#slide-one-animation {
  aspect-ratio: 10/9 !important;
  bottom: 0.5rem;
}

#slide-two-animation {
  aspect-ratio: 10/9 !important;
}

#slide-three-animation {
  aspect-ratio: 1 !important;
}

#slide-four-animation {
  aspect-ratio: 9/8 !important;
}
</style>
